import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Remove as RemoveIcon,
  WorkspacePremium as CrownIcon,
  Lock as LockIcon,
  AudioFile as AudioIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  DirectionsRun as MovesIcon,
  Public as WorldIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Asset } from '../../types/asset.types';
import AssetSearch from '../search/AssetSearch';
import AssetThumbnail from '../common/AssetThumbnail';
import { formatCompositeHfn } from '../../utils/compositeAssetFormatter';

// Composite requires exactly one asset per layer: Song (G), Star (S), Look (L), Move (M), World (W)
const REQUIRED_COMPONENT_LAYERS = ['G', 'S', 'L', 'M', 'W'] as const;

// Layer icons and colors for visual identification
const LAYER_CONFIG: Record<string, { icon: React.ElementType; color: string; name: string }> = {
  G: { icon: AudioIcon, color: '#1976d2', name: 'Song' },
  S: { icon: PersonIcon, color: '#9c27b0', name: 'Star' },
  L: { icon: PaletteIcon, color: '#f57c00', name: 'Look' },
  M: { icon: MovesIcon, color: '#388e3c', name: 'Move' },
  W: { icon: WorldIcon, color: '#00796b', name: 'World' },
  B: { icon: CrownIcon, color: '#d32f2f', name: 'Branded' },
  P: { icon: LockIcon, color: '#7b1fa2', name: 'Personalize' },
};

interface CompositeAssetSelectionProps {
  onComponentsSelected: (components: Asset[]) => void;
  onValidationChange?: (errors: string[]) => void;
  initialComponents?: Asset[];
  targetLayer?: string;
  layerName?: string;
  categoryCode?: string;
  subcategoryCode?: string;
}

interface SearchParams {
  query: string;
  layer: string;
}

interface RightsVerificationResult {
  assetId: string;
  status: 'approved' | 'denied' | 'restricted';
  message?: string;
  restrictions?: string[];
}

/** Normalize layer/category/subcategory to string (handles objects with .code from formData). */
function toCodeString(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (value != null && typeof value === 'object' && 'code' in value && typeof (value as { code: unknown }).code === 'string') {
    return (value as { code: string }).code;
  }
  return value != null ? String(value).trim() : '';
}

const CompositeAssetSelection: React.FC<CompositeAssetSelectionProps> = ({
  onComponentsSelected,
  onValidationChange,
  initialComponents = [],
  targetLayer = 'C',
  layerName = 'Composite',
  categoryCode = 'FUL', // Default to Full_Composites
  subcategoryCode = 'POP', // Default to Pop
}) => {
  const initialSlots = React.useMemo(() => {
    const s: Record<string, Asset | undefined> = { G: undefined, S: undefined, L: undefined, M: undefined, W: undefined };
    if (Array.isArray(initialComponents)) {
      for (const asset of initialComponents) {
        if (asset?.layer && (REQUIRED_COMPONENT_LAYERS as readonly string[]).includes(asset.layer)) {
          s[asset.layer] = asset;
        }
      }
    }
    return s;
  }, [initialComponents]);
  const [slots, setSlots] = useState<Record<string, Asset | undefined>>(initialSlots);
  const [browsingForSlot, setBrowsingForSlot] = useState<string | null>(null);
  const selectedComponents = useMemo(
    () => REQUIRED_COMPONENT_LAYERS.map(l => slots[l]).filter((a): a is Asset => !!a),
    [slots]
  );
  const [searchParams, setSearchParams] = useState<SearchParams>({
    query: '',
    layer: '',
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [, setRightsValidation] = useState<{
    [assetId: string]: RightsVerificationResult;
  }>({});

  const updateValidationErrors = useCallback((errors: string[]) => {
    setValidationErrors(errors);
    if (onValidationChange) {
      onValidationChange(errors);
    }
  }, [onValidationChange]);

  useEffect(() => {
    const ordered = REQUIRED_COMPONENT_LAYERS.map(l => slots[l]).filter((a): a is Asset => !!a);
    if (ordered.length === 5) {
      onComponentsSelected(REQUIRED_COMPONENT_LAYERS.map(l => slots[l]!) as Asset[]);
    }
  }, [slots, onComponentsSelected]);

  useEffect(() => {
    const errors = validateComponents(selectedComponents);
    updateValidationErrors(errors);
  }, [selectedComponents, updateValidationErrors]);

  const validateComponents = (components: Asset[]): string[] => {
    const errors: string[] = [];

    // Check if components array is valid
    if (!Array.isArray(components)) {
      errors.push('Invalid components data: expected array');
      return errors;
    }

    // Composite requires exactly 5 components: one Song (G), Star (S), Look (L), Move (M), World (W)
    if (components.length !== 5) {
      errors.push('Select exactly one asset per layer: Song (G), Star (S), Look (L), Move (M), World (W)');
    }
    const layersPresent = new Set(components.map(a => a?.layer).filter(Boolean));
    if (layersPresent.size < 5 || !REQUIRED_COMPONENT_LAYERS.every(l => layersPresent.has(l))) {
      errors.push('You must select exactly one Song (G), one Star (S), one Look (L), one Move (M), and one World (W).');
    }

    // Validate each component has required fields
    const invalidStructure = components.filter(asset => {
      return !asset || typeof asset !== 'object' || !asset.id || !asset.layer;
    });

    if (invalidStructure.length > 0) {
      errors.push(
        `${invalidStructure.length} component(s) have invalid or missing required data (id, layer)`
      );
    }

    // Check for components with missing/null/invalid layer values
    const missingLayers = components.filter(asset => {
      return (
        asset &&
        (!asset.layer ||
          typeof asset.layer !== 'string' ||
          asset.layer.trim() === '')
      );
    });

    if (missingLayers.length > 0) {
      errors.push(
        `Components with missing or invalid layer detected: ${missingLayers
          .map(
            asset =>
              `${asset.friendlyName || asset.name || asset.id} (layer: ${
                asset.layer || 'missing'
              })`
          )
          .join(', ')}`
      );
    }

    // Check if all components have a layer in allowed layers (excluding those with missing layers)
    const componentsWithValidLayers = components.filter(
      asset =>
        asset &&
        asset.layer &&
        typeof asset.layer === 'string' &&
        asset.layer.trim() !== ''
    );

    const invalidComponents = componentsWithValidLayers.filter(
      asset => !REQUIRED_COMPONENT_LAYERS.includes(asset.layer.trim().toUpperCase() as typeof REQUIRED_COMPONENT_LAYERS[number])
    );

    if (invalidComponents.length > 0) {
      errors.push(
        `Components from incompatible layers detected: ${invalidComponents
          .map(
            asset =>
              `${asset.friendlyName || asset.name || asset.id} (layer: ${
                asset.layer
              })`
          )
          .join(', ')}. Required: exactly one per layer G, S, L, M, W.`
      );
    }

    // Check for duplicate components
    const componentIds = components.map(asset => asset.id).filter(Boolean);
    const duplicateIds = componentIds.filter(
      (id, index) => componentIds.indexOf(id) !== index
    );

    if (duplicateIds.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateIds));
      errors.push(
        `Duplicate components detected: ${uniqueDuplicates.join(', ')}`
      );
    }

    // Check for components with missing names (for HFN generation)
    const missingNames = components.filter(
      asset => asset && asset.id && !asset.friendlyName && !asset.name
    );

    if (missingNames.length > 0) {
      errors.push(
        `Components missing display names: ${missingNames
          .map(asset => asset.id)
          .join(', ')}. Names are required for composite HFN generation.`
      );
    }

    return errors;
  };

  // Step 4: Generate composite address in the format [Layer].[CategoryCode].[SubCategoryCode].[Sequential]:[MFA addresses]
  const generateCompositeHFN = (
    components: Asset[],
    sequential: string = '001'
  ): string => {
    const layerPart = toCodeString(targetLayer);
    const categoryPart = toCodeString(categoryCode);
    const subcategoryPart = toCodeString(subcategoryCode);

    if (components.length === 0) {
      return `${layerPart}.${categoryPart}.${subcategoryPart}.${sequential}`;
    }

    // Extract component HFNs from humanFriendlyName (API always returns humanFriendlyName)
    const componentHFNs = components
      .map(asset => (asset as any).humanFriendlyName ?? asset.friendlyName ?? '')
      .filter(Boolean)
      .join('+');

    return `${layerPart}.${categoryPart}.${subcategoryPart}.${sequential}:${componentHFNs}`;
  };

  // REMOVED: registerCompositeAsset function - registration now handled by RegisterAssetPage unified workflow
  // Step 5: Optimized composite preview generation with performance logging

  const handleSelectForSlot = useCallback(
    (layer: string, asset: Asset) => {
      if (!REQUIRED_COMPONENT_LAYERS.includes(layer as typeof REQUIRED_COMPONENT_LAYERS[number])) return;
      if (asset.layer !== layer) {
        toast.warn(`Please select a ${LAYER_CONFIG[layer]?.name ?? layer} (${layer}) layer asset for this slot.`);
        return;
      }
      // Use search result as-is so UI updates immediately without blocking on getAssetById
      setRightsValidation(prev => ({
        ...prev,
        [asset.id]: { assetId: asset.id, status: 'approved', message: 'Rights verification bypassed' },
      }));
      const newSlots = { ...slots, [layer]: asset };
      setSlots(newSlots);
      setBrowsingForSlot(null);
      if (REQUIRED_COMPONENT_LAYERS.every(l => newSlots[l])) {
        onComponentsSelected(REQUIRED_COMPONENT_LAYERS.map(l => newSlots[l]!) as Asset[]);
      }
      toast.success(`Selected ${asset.friendlyName || asset.name} for ${LAYER_CONFIG[layer]?.name ?? layer}`);
    },
    [slots, onComponentsSelected]
  );

  const handleAddComponent = useCallback(
    async (asset: Asset) => {
      if (!browsingForSlot) {
        toast.info('Click "Browse" next to a layer slot (Song, Star, Look, Move, World) to select an asset.');
        return;
      }
      if (asset.layer !== browsingForSlot) {
        toast.warn(`Please select a ${LAYER_CONFIG[browsingForSlot]?.name ?? browsingForSlot} (${browsingForSlot}) layer asset for this slot.`);
        return;
      }
      await handleSelectForSlot(browsingForSlot, asset);
    },
    [browsingForSlot, handleSelectForSlot]
  );

  const handleClearSlot = useCallback((layer: string) => {
    const asset = slots[layer];
    if (asset) {
      setRightsValidation(prev => {
        const updated = { ...prev };
        delete updated[asset.id];
        return updated;
      });
    }
    setSlots(prev => ({ ...prev, [layer]: undefined }));
  }, [slots]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Composite Components
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select exactly one Song (G), Star (S), Look (L), Move (M), and World (W). Click Browse next to each slot to pick an asset.
      </Typography>

      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Five fixed slots: Song, Star, Look, Move, World */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {REQUIRED_COMPONENT_LAYERS.map(layer => {
          const config = LAYER_CONFIG[layer];
          const asset = slots[layer];
          const IconComponent = config?.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={layer}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {IconComponent && <IconComponent sx={{ color: config.color, fontSize: 20 }} />}
                  <Typography variant="subtitle2" fontWeight="bold">
                    {config?.name ?? layer} ({layer})
                  </Typography>
                </Box>
                {asset ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <AssetThumbnail asset={asset} width={36} height={36} />
                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {formatCompositeHfn(asset.friendlyName || asset.name)}
                    </Typography>
                    <IconButton size="small" onClick={() => handleClearSlot(layer)} color="error" aria-label={`Clear ${layer}`}>
                      <RemoveIcon />
                    </IconButton>
                    <Button size="small" variant="outlined" onClick={() => setBrowsingForSlot(layer)}>
                      Change
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => setBrowsingForSlot(layer)}
                    startIcon={<SearchIcon />}
                  >
                    Browse {config?.name ?? layer}
                  </Button>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {selectedComponents.length === 5 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom display="block">
            Composite HFN Preview:
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.75rem', p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            {generateCompositeHFN(REQUIRED_COMPONENT_LAYERS.map(l => slots[l]!).filter(Boolean) as Asset[])}
          </Typography>
        </Alert>
      )}

      {/* Asset search: shown when browsing for a slot, filtered to that layer */}
      {browsingForSlot && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select a {LAYER_CONFIG[browsingForSlot]?.name ?? browsingForSlot} ({browsingForSlot}) asset
          </Typography>
          <Button size="small" onClick={() => setBrowsingForSlot(null)} sx={{ mb: 1 }}>
            Cancel
          </Button>
          <AssetSearch
            key={browsingForSlot}
            onAssetSelect={handleAddComponent}
            initialParams={{ layer: browsingForSlot, search: searchParams.query || '' }}
            searchParams={{ query: searchParams.query, layer: browsingForSlot }}
            setSearchParams={setSearchParams}
            showLayerFilter={false}
            fullWidth={true}
            hideSearchBox={false}
            hideFilters={true}
            excludeLayers={['C']}
            hideCardActions={true}
          />
        </Box>
      )}

      {/* Action buttons now handled by RegisterAssetPage - no redundant buttons needed */}

      {/* Registration status now handled by unified workflow */}
    </Box>
  );
};

export default CompositeAssetSelection;
